import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { MultiSelectDropDown } from 'src/components/common/multiselect-dropdown';
import { BrandsProducts } from 'src/interfaces/new-channel-partner/channelPartner';
import { brandApi } from 'src/api/brand';
import { IdValue } from 'src/interfaces/common/IdValue';
import { productApi } from 'src/api/products';
import { InputField } from 'src/components/input-field';
import { MenuProps } from 'src/components/masters/customCss';
import { intersectionArray } from 'src/components/masters/customFunctions';
import { Status } from 'src/enums/common';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';

interface AddBrandsProductsProps {
  open: boolean;
  onClose: () => void;
  isAdd?: boolean;
  addBrand?: (data: BrandsProducts) => Promise<boolean>;
  updateBrand?: (data: BrandsProducts) => Promise<boolean>;
  cpBrandsProducts?: BrandsProducts[];
  scope?: string;
  brandsProducts?: BrandsProducts[];
}

export const AddBrandsProducts: FC<AddBrandsProductsProps> = (props) => {
  const { open, onClose, updateBrand, addBrand,
    isAdd, cpBrandsProducts, scope, brandsProducts } = props;
  const [brandList, setBrandList] = useState<IdValue[]>([]);
  const [productDetails, setProductDetails] = useState<IdValue[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [filterProducts, setFilterProducts] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [isAvailable, setIsAvailable] = useState<string>('Loading');

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      setSelectedProducts([]);
      setSelectedBrand('');
      onClose();
    }
  };

  const isDisable = () => {
    const productNames = selectedProducts.map((item) => item.name);
    return !selectedBrand || !selectedProducts.length
            || (!isAdd && (brandsProducts[0]?.products.every(
              (val) => productNames?.includes(val.name)
            )
            && brandsProducts[0]?.products.length === productNames?.length));
  };

  const handleDelete = (item) => {
    const newProductList = selectedProducts.filter((Product) => Product.id !== item.id);
    setSelectedProducts([...newProductList]);
  };

  const getBrandAndProductsDetails = async () => {
    if (scope === 'Technician') {
      if (isAdd) {
        const selectedItems = brandsProducts?.map((x) => x.brandId);
        const availableServices = cpBrandsProducts?.filter(
          (brand) => !selectedItems.includes(brand.brandId)
        );
        const tempBrandData = [];
        availableServices.forEach((item) => {
          tempBrandData.push({ id: item.brandId, name: item.name });
        });
        setBrandList(tempBrandData);
        setIsAvailable(cpBrandsProducts.length !== 0 ? 'Success' : 'Fail');
      } else {
        const tempProductData = cpBrandsProducts.filter(
          (item) => item.brandId === brandsProducts[0].brandId
        )[0];
        const tempData = [];
        tempProductData?.products.forEach((item) => {
          tempData.push({ id: item.productId, name: item.name });
        });
        setProductDetails(tempData);
        setFilterProducts(tempProductData?.products?.map((product) => ({
          ...product,
          id: product.productId,
          value: product.name,
        })));
        setBrandList([{ id: brandsProducts[0].brandId, name: brandsProducts[0].name }]);
        setSelectedBrand(brandsProducts[0].brandId);
        const selectedProductItems = brandsProducts[0].products?.map((x) => x.name);
        const intersectedValue = intersectionArray(tempProductData?.products, selectedProductItems, 'name');
        setSelectedProducts(intersectedValue?.map((product) => ({
          ...product,
          id: product.productId,
          name: product.name,
        })));
        setIsAvailable(tempProductData?.products?.length !== 0 && brandsProducts.length !== 0 ? 'Success' : 'Fail');
      }
    } else {
      const paginationObjectForProduct: PaginationAndFilter = {
        filters: [
          {
            property: 'status',
            operator: 'in',
            value: [
              Status.PUBLISH,
            ],
          },
        ],
        pageSize: 9999,
        pageNumber: 1,
        sort: -1,
        sortBy: ''
      };
      const productData = await productApi.getProductsByPaginationAndFilters(
        paginationObjectForProduct
      );
      const productResult = productData?.responseData?.records;
      setProductDetails(productResult);
      setFilterProducts(productResult?.map((product) => ({
        ...product,
        id: product.id,
        value: product.name,
      })));
      if (isAdd) {
        const paginationObjectForBrand: PaginationAndFilter = {
          filters: [
            {
              property: 'status',
              operator: 'in',
              value: [
                Status.PUBLISH,
              ],
            },
          ],
          pageSize: 9999,
          pageNumber: 1,
          sort: -1,
          sortBy: ''
        };
        const brandData = await brandApi.getBrandsByPaginationAndFilters(
          paginationObjectForBrand
        );
        const brandResult = brandData?.responseData?.records;
        const selectedItems = brandsProducts?.map((x) => x.brandId);
        const availableServices = brandResult?.filter(
          (brand) => !selectedItems.includes(brand.id)
        );
        setBrandList(availableServices);
        setIsAvailable(productResult?.length !== 0 && brandResult.length !== 0 ? 'Success' : 'Fail');
      } else {
        setBrandList([{ id: brandsProducts[0].brandId, name: brandsProducts[0].name }]);
        setSelectedBrand(brandsProducts[0].brandId);
        const selectedProductItems = brandsProducts[0].products?.map((x) => x.name);
        const intersectedValue = intersectionArray(productResult, selectedProductItems, 'name');
        setSelectedProducts(intersectedValue?.map((product) => ({
          ...product,
          id: product.id,
          name: product.name,
        })));
        setIsAvailable(productResult?.length !== 0 && brandsProducts.length !== 0 ? 'Success' : 'Fail');
      }
    }
  };

  useEffect(() => {
    getBrandAndProductsDetails().catch(console.error);
  }, [brandsProducts, cpBrandsProducts]);

  // Handles multi select dropdown selection list
  const multiSelectionHandler = (event) => {
    // Selected id from dropdown
    const selectedId = event.target.value[event.target.value.length - 1]
      ? event.target.value[event.target.value.length - 1].trim()
      : null;
    const selected = productDetails?.filter((product) => product.id === selectedId)[0];
    const productExist = selectedProducts.filter(
      (product) => product.id === selectedId
    ).length > 0;
    if (!productExist) {
      setSelectedProducts(
        [...selectedProducts, { name: selected.name, id: selected.id }]
      );
    } else {
      const newItems = selectedProducts.filter((product) => product.id !== selectedId);
      setSelectedProducts(newItems);
    }
  };

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    if (scope === 'Technician') {
      setSelectedProducts([]);
      const tempProductData = cpBrandsProducts?.filter(
        (item) => item.brandId === event.target.value
      )[0];
      const tempData = [];
      tempProductData?.products.forEach((item) => {
        tempData.push({ id: item.productId, name: item.name });
      });
      setProductDetails(tempData);
      setFilterProducts(tempProductData?.products?.map((product) => ({
        ...product,
        id: product.productId,
        value: product.name,
      })));
    }
  };

  const addBrandProducts = async (e) => {
    const filterValue = brandList.filter((item) => item.id === selectedBrand);
    const tempArray = [];
    selectedProducts.map((item) => tempArray.push({ productId: item.id, name: item.name }));
    if (isAdd) {
      const inputOption: BrandsProducts = {
        brandId: selectedBrand,
        name: filterValue[0].name,
        products: tempArray
      };
      const addResult = await addBrand(inputOption);
      if (addResult) {
        closeDialog(e, '');
      }
    } else {
      const inputOption: BrandsProducts = {
        brandId: brandsProducts[0].brandId,
        name: brandsProducts[0].name,
        products: tempArray
      };
      const updateResult = await updateBrand(inputOption);
      if (updateResult) {
        closeDialog(e, '');
      }
    }
  };

  return (
    <Dialog
      fullWidth
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        Brands & Products
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      { isAvailable === 'Loading' && (
        <>
          <Typography
            sx={{
              ml: 4,
              mb: 4
            }}
            variant="body2"
          >
            Loading...
          </Typography>
        </>
      )}
      { isAvailable === 'Fail' && (
      <Typography
        sx={{
          ml: 4,
          mb: 4
        }}
        color="red"
        variant="body2"
      >
        Brands or products are not available
      </Typography>
      )}
      { isAvailable === 'Success' && (
      <>
        <DialogContent
          sx={{
            mt: 0
          }}
        >
          <Grid
            container
            spacing={1}
          >
            <Grid
              xs={12}
              item
            >
              <InputField
                fullWidth
                placeholder="Brand"
                name="brandId"
                label="Select Brand"
                required
                select
                disabled={!isAdd}
                onChange={handleBrandChange}
                sx={{ mb: 1 }}
                SelectProps={{ MenuProps }}
                value={selectedBrand}
              >
                {brandList?.map((brand) => (
                  <MenuItem
                    key={brand.id}
                    value={brand.id}
                  >
                    {brand.name}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
            <Grid
              xs={12}
              item
            >
              <MultiSelectDropDown
                label="Select Products"
                index="1"
                disabled={isAdd && !selectedBrand}
                options={filterProducts}
                onSelect={multiSelectionHandler}
                selectedOptions={selectedProducts.map((item) => item.name)}
              />
            </Grid>
            <Grid
              xs={12}
              item
            >
              {selectedProducts?.length > 0 ? (
                <Box
                  maxHeight={200}
                  sx={{
                    height: 200,
                    m: 1,
                    mb: 0
                  }}
                >
                  {selectedProducts.map((item) => (
                    <Chip
                      color="primary"
                      variant="outlined"
                      key={item.id}
                      sx={{ pr: 1, borderRadius: '20px', m: 1 }}
                      label={item.name}
                      tabIndex={-1}
                      onDelete={() => {
                        handleDelete(item);
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  maxHeight={200}
                  sx={{
                    height: 200,
                    pt: 10,
                    mt: 1,
                    pr: 2,
                    textAlign: 'center',
                    border: '.5px solid',
                  }}
                >
                  <Typography
                    color="textPrimary"
                    variant="caption"
                  >
                    No Products Added
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'right'
          }}
        >
          <Button
            color="primary"
            onClick={(e) => {
              closeDialog(e, '');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={isDisable()}
            color="primary"
            onClick={(e) => {
              addBrandProducts(e);
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </>
      )}
    </Dialog>
  );
};

AddBrandsProducts.defaultProps = {
  open: false,
};

AddBrandsProducts.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  addBrand: PropTypes.func,
  updateBrand: PropTypes.func,
  // @ts-ignore
  isAdd: PropTypes.bool,
  cpBrandsProducts: PropTypes.array,
  scope: PropTypes.string,
  brandsProducts: PropTypes.array
};
